// src/components/ArticulationSideView.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { createPortal } from "react-dom";
console.log("✅ ArticulationSideView.jsx LOADED", new Date().toISOString());

const clamp01 = (x) => Math.max(0, Math.min(1, x));
const lerp = (a, b, t) => a + (b - a) * t;

// --------------------------- base pose + presets ----------------------------

const BASE = {
  tipX: 0.70,
  tipY: 0.62,
  midX: 0.52,
  midY: 0.72,
  backX: 0.26,
  backY: 0.80,
  contact: "none", // teeth | ridge | palate | velum | lips | none
  lipRound: 0.12,
  lipOpen: 0.22,
  airflow: "none", // none | smooth | noisy | burst
  voicing: false,
};

const PRESETS = {
  neutral: BASE,

  dental: { ...BASE, tipX: 0.9, tipY: 0.56, midX: 0.66, midY: 0.68, contact: "teeth", airflow: "burst" },
  alveolar: { ...BASE, tipX: 0.82, tipY: 0.5, midX: 0.62, midY: 0.64, contact: "ridge", airflow: "burst" },
  palatalized: { ...BASE, tipX: 0.78, tipY: 0.58, midX: 0.62, midY: 0.5, contact: "palate", airflow: "smooth" },
  velarized: { ...BASE, backX: 0.26, backY: 0.56, midX: 0.5, midY: 0.66, contact: "velum", airflow: "smooth" },

  fricative: { ...BASE, tipX: 0.82, tipY: 0.52, midX: 0.6, midY: 0.64, contact: "ridge", airflow: "noisy" },
  approximant: { ...BASE, lipRound: 0.55, lipOpen: 0.22, airflow: "smooth" },
  aspirated: { ...BASE, contact: "ridge", airflow: "burst" },
  voiced: { ...BASE, voicing: true },
};

const CAPTION = {
  neutral: "Neutral position",
  dental: "Tongue tip touches the teeth (dental)",
  alveolar: "Tongue tip touches the ridge behind the teeth (alveolar)",
  palatalized: "Tongue body raised toward the hard palate (palatalized)",
  velarized: "Tongue back raised toward the soft palate (velarized)",
  rounded: "Lips rounded/pursed",
  fricative: "Narrow gap → noisy airflow (fricative)",
  aspirated: "Burst of air after release (aspiration)",
  approximant: "Soft constriction, no friction",
  voiced: "Voicing on (vocal folds vibrating)",
};

function useTweenedPose(targetPose, durationMs = 240) {
  const [pose, setPose] = useState(targetPose);

  useEffect(() => {
    let raf = null;
    const start = performance.now();
    const from = pose;
    const to = targetPose;

    const step = (now) => {
      const t = clamp01((now - start) / durationMs);
      const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

      setPose({
        tipX: lerp(from.tipX, to.tipX, eased),
        tipY: lerp(from.tipY, to.tipY, eased),
        midX: lerp(from.midX, to.midX, eased),
        midY: lerp(from.midY, to.midY, eased),
        backX: lerp(from.backX, to.backX, eased),
        backY: lerp(from.backY, to.backY, eased),
        lipRound: lerp(from.lipRound, to.lipRound, eased),
        lipOpen: lerp(from.lipOpen, to.lipOpen, eased),
        contact: t < 1 ? from.contact : to.contact,
        airflow: t < 1 ? from.airflow : to.airflow,
        voicing: t < 1 ? from.voicing : to.voicing,
      });

      if (t < 1) raf = requestAnimationFrame(step);
    };

    raf = requestAnimationFrame(step);
    return () => raf && cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    targetPose.tipX,
    targetPose.tipY,
    targetPose.midX,
    targetPose.midY,
    targetPose.backX,
    targetPose.backY,
    targetPose.lipRound,
    targetPose.lipOpen,
    targetPose.contact,
    targetPose.airflow,
    targetPose.voicing,
  ]);

  return pose;
}

// ------------------------------ anatomy layout ------------------------------

const VB = { w: 1024, h: 1024 };
function pt(u, v) {
  return { x: u * VB.w, y: v * VB.h };
}

const LANDMARKS = {
  lips: pt(0.2, 0.51),
  teeth: pt(0.28, 0.46),
  ridge: pt(0.34, 0.4),
  palate: pt(0.5, 0.29),
  velum: pt(0.73, 0.3),
};

function buildTonguePath(p) {
  const x = (u) => u * VB.w;
  const y = (v) => v * VB.h;

  const back = { x: x(p.backX), y: y(p.backY) };
  const mid = { x: x(p.midX), y: y(p.midY) };
  const tip = { x: x(p.tipX), y: y(p.tipY) };

  const underMid = { x: x(0.58), y: y(0.78) };
  const underBack = { x: x(0.34), y: y(0.84) };

  return `
    M ${back.x} ${back.y}
    Q ${mid.x} ${mid.y} ${tip.x} ${tip.y}
    Q ${x(0.74)} ${y(0.66)} ${underMid.x} ${underMid.y}
    Q ${x(0.50)} ${y(0.90)} ${underBack.x} ${underBack.y}
    Q ${x(0.26)} ${y(0.82)} ${back.x} ${back.y}
    Z
  `;
}

// Default “centerline” curve from a pose (used for consonant-ish/auto vowel pose)
function buildTongueCenterline(p) {
  const x = (u) => u * VB.w;
  const y = (v) => v * VB.h;

  const back = { x: x(p.backX), y: y(p.backY) };
  const mid = { x: x(p.midX), y: y(p.midY) };
  const tip = { x: x(p.tipX), y: y(p.tipY) };

  return `M ${back.x} ${back.y} Q ${mid.x} ${mid.y} ${tip.x} ${tip.y}`;
}

// ------------------------------ vowel overlay -------------------------------

function vowelPose({ frontness, height, rounding }) {
  const isFront = frontness === "front";
  const isBack = frontness === "back";
  const isCentral = frontness === "central" || (!isFront && !isBack);

  const isClose = height === "close";
  const isOpen = height === "open";
  const isMid = height === "mid" || (!isClose && !isOpen);

  let p = { ...BASE };

  // Back anchor
  p.backX = isBack ? 0.28 : isFront ? 0.25 : 0.26;
  p.backY = isClose ? 0.62 : isMid ? 0.72 : 0.82;

  // Mid (main shaping)
  p.midX = isFront ? 0.6 : isCentral ? 0.53 : 0.46;
  p.midY = isClose ? 0.46 : isMid ? 0.6 : 0.72;

  // Tip
  p.tipX = isFront ? 0.78 : isCentral ? 0.74 : 0.7;
  p.tipY = isClose ? 0.58 : isMid ? 0.62 : 0.66;

  if (rounding === "rounded") p.lipRound = 0.55;

  p.contact = "none";
  p.airflow = "none";
  p.voicing = false;

  return p;
}

function isValidCurve3(curve) {
  if (!curve) return false;
  const { start, mid, end } = curve;
  const okPoint = (p) =>
    p &&
    Number.isFinite(p.x) &&
    Number.isFinite(p.y) &&
    p.x >= 0 &&
    p.x <= 1 &&
    p.y >= 0 &&
    p.y <= 1;
  return okPoint(start) && okPoint(mid) && okPoint(end);
}

// Build a quadratic curve from manual 3 landmarks
function buildCurveFrom3Points(curve) {
  const s = pt(curve.start.x, curve.start.y);
  const m = pt(curve.mid.x, curve.mid.y);
  const e = pt(curve.end.x, curve.end.y);
  return `M ${s.x} ${s.y} Q ${m.x} ${m.y} ${e.x} ${e.y}`;
}

// ------------------------------ tags → pose --------------------------------

function parseTags(visTags) {
  const tags = (visTags || []).map(String);
  const has = (t) => tags.includes(t);

  const allPlace = tags.filter((t) => t.startsWith("place:")).map((t) => t.split(":")[1]);
  const placePrimary = allPlace[0] || null;

  const airflow =
    has("air:burst") ? "burst" : has("air:noisy") ? "noisy" : has("air:smooth") ? "smooth" : "none";

  const voicing = has("phon:voiced");
  const palatalized = has("sec:palatalized");
  const velarized = has("sec:velarized");
  const lessAspiratedRU = has("hint:less-aspirated-ru");
  const devoiceFinal = has("phon:devoice-final");

  const secTargets = [];
  if (palatalized) secTargets.push("palate");
  if (velarized) secTargets.push("velum");

  return {
    allPlace,
    placePrimary,
    airflow,
    voicing,
    palatalized,
    velarized,
    lessAspiratedRU,
    devoiceFinal,
    secTargets,
  };
}

function poseFromTags(visTags) {
  const t = parseTags(visTags);
  let p = { ...BASE };

  if (t.placePrimary === "teeth") {
    p = { ...p, ...PRESETS.dental, contact: "teeth" };
  } else if (t.placePrimary === "ridge") {
    p = { ...p, ...PRESETS.alveolar, contact: "ridge" };
  } else if (t.placePrimary === "palate") {
    p = { ...p, ...PRESETS.palatalized, contact: "palate" };
  } else if (t.placePrimary === "velum") {
    p = { ...p, ...PRESETS.velarized, contact: "velum" };
  } else if (t.placePrimary === "lips") {
    p = { ...p, contact: "lips", tipX: 0.7, tipY: 0.62, midX: 0.52, midY: 0.72, backX: 0.3, backY: 0.8 };
  }

  if (t.airflow !== "none") p.airflow = t.airflow;

  if (t.palatalized) {
    p = { ...p, midY: Math.max(0.38, p.midY - 0.18), midX: Math.min(0.62, p.midX + 0.06) };
  }
  if (t.velarized) {
    p = { ...p, backY: Math.max(0.52, p.backY - 0.22), midX: Math.max(0.48, p.midX - 0.04) };
  }

  p.voicing = !!t.voicing;
  return { pose: p, meta: t };
}

function airflowConfig(airflow, lessAspiratedRU) {
  if (airflow === "smooth") return { kind: "smooth", opacity: 0.34 };
  if (airflow === "noisy") return { kind: "noisy", opacity: 0.46 };
  if (airflow === "burst") return { kind: "burst", opacity: lessAspiratedRU ? 0.55 : 0.82 };
  return null;
}

// ------------------------------ compare helpers -----------------------------

function isNonEmptyTags(tags) {
  return Array.isArray(tags) && tags.length > 0;
}

function placesFromTags(tags) {
  const ts = (tags || []).map(String);
  return ts
    .filter((t) => t.startsWith("place:"))
    .map((t) => t.split(":")[1])
    .filter(Boolean);
}

function secTargetsFromTags(tags) {
  const ts = (tags || []).map(String);
  const out = [];
  if (ts.includes("sec:palatalized")) out.push("palate");
  if (ts.includes("sec:velarized")) out.push("velum");
  return out;
}

// ------------------------------ modal ---------------------------------------

function useLockBodyScroll(locked) {
  useEffect(() => {
    if (!locked) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev || "";
    };
  }, [locked]);
}

function ZoomModal({ open, title = "Articulation", children, onClose }) {
  useLockBodyScroll(open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[999]">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-5xl rounded-2xl bg-white shadow-2xl ring-1 ring-black/5">
          <div className="flex items-center justify-between gap-3 border-b p-4">
            <div className="text-sm font-semibold text-slate-900">{title}</div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              Close
            </button>
          </div>

          <div className="p-4">
            <div className="mx-auto w-full max-w-4xl">{children}</div>
            <div className="mt-3 text-xs text-slate-500">
              Tip: press <span className="rounded border bg-slate-50 px-1">Esc</span> to close.
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ------------------------------ component -----------------------------------

export default function ArticulationSideView({
  preset = null,
  visTags = null,

  // NEW: NO vs L1 consonant compare
  // visCompare: { no:{tags:[...]}, l1:{tags:[...]}, noLabel?, l1Label? }
  visCompare = null,

  // Vowel overlay (NO vs L1)
  visVowel = null,

  baseSrc = `${process.env.REACT_APP_USER_API_URL}/uploads/diagrams/mouth-base.png`,

  label,
  className = "",
  showLandmarks = true,

  showTongue = false,

  enableZoom = true,
  defaultZoomOpen = false,

  // NOTE: accepted (SpeechSounds passes it), but currently not used
  // (kept for future styling variants)
  variant = "classic",
}) {
  console.log("✅ ArticulationSideView RENDER", { preset, visTags, visVowel, visCompare, variant });
  const [zoomOpen, setZoomOpen] = useState(!!defaultZoomOpen);

  const hasCompareVis =
    !!visCompare && (isNonEmptyTags(visCompare?.no?.tags) || isNonEmptyTags(visCompare?.l1?.tags));
  const hasConsonantVis = !hasCompareVis && Array.isArray(visTags) && visTags.length > 0;
  const hasVowelVis = !!(visVowel && (visVowel.no || visVowel.l1));

  // Pose is only relevant for "single visTags" mode (RU-style). In compare mode we don't animate pose.
  const { pose: targetPose, meta } = useMemo(() => {
    if (hasConsonantVis) return poseFromTags(visTags);
    const p = preset && PRESETS[preset] ? PRESETS[preset] : PRESETS.neutral;
    return { pose: p, meta: parseTags([]) };
  }, [preset, visTags, hasConsonantVis]);

  const pose = useTweenedPose(targetPose, 240);
  const tonguePath = useMemo(() => buildTonguePath(pose), [pose]);

  // single-mode dots (RU-style)
  const primaryPlaces = meta?.allPlace?.length
    ? meta.allPlace
    : pose.contact && pose.contact !== "none"
      ? [pose.contact]
      : [];
  const primaryDots = primaryPlaces.map((k) => LANDMARKS[k]).filter(Boolean);

  const secondaryPlaces = meta?.secTargets || [];
  const secondaryDots = secondaryPlaces.map((k) => LANDMARKS[k]).filter(Boolean);

  const air = airflowConfig(pose.airflow, meta?.lessAspiratedRU);

  // compare-mode dots (NO vs L1)
  const compareNoPlaces = hasCompareVis ? placesFromTags(visCompare?.no?.tags) : [];
  const compareL1Places = hasCompareVis ? placesFromTags(visCompare?.l1?.tags) : [];

  const compareNoDots = compareNoPlaces.map((k) => LANDMARKS[k]).filter(Boolean);
  const compareL1Dots = compareL1Places.map((k) => LANDMARKS[k]).filter(Boolean);

  const compareNoSecDots = hasCompareVis ? secTargetsFromTags(visCompare?.no?.tags).map((k) => LANDMARKS[k]).filter(Boolean) : [];
  const compareL1SecDots = hasCompareVis ? secTargetsFromTags(visCompare?.l1?.tags).map((k) => LANDMARKS[k]).filter(Boolean) : [];

  const captionText = useMemo(() => {
    if (hasCompareVis) return "NO vs L1 (places compared)";
    if (hasConsonantVis) return `Tags: ${visTags.join(", ")}`;
    if (hasVowelVis) return "Vowel tongue curve (NO vs L1)";
    if (preset) return CAPTION[preset] || CAPTION.neutral;
    return "Articulation";
  }, [preset, visTags, hasConsonantVis, hasVowelVis, hasCompareVis]);

  const openZoom = useCallback(() => {
    if (!enableZoom) return;
    setZoomOpen(true);
  }, [enableZoom]);

  const closeZoom = useCallback(() => setZoomOpen(false), []);

  const DiagramSvg = ({ large = false }) => {
    const dotR = large ? 9 : 7;
    const labelSize = large ? 26 : 22;
    const haloOuter = large ? 7 : 6;
    const haloInner = large ? 5 : 4;

    // ---------- Vowel curves (NO vs L1) ----------
    const noCurveRaw = visVowel?.no?.curve || null;
    const l1CurveRaw = visVowel?.l1?.curve || null;

    const noCurve = isValidCurve3(noCurveRaw) ? noCurveRaw : null;
    const l1Curve = isValidCurve3(l1CurveRaw) ? l1CurveRaw : null;

    const vowelNoPose = !noCurve && hasVowelVis && visVowel?.no ? vowelPose(visVowel.no) : null;
    const vowelL1Pose = !l1Curve && hasVowelVis && visVowel?.l1 ? vowelPose(visVowel.l1) : null;

    const vowelNoPath = noCurve ? buildCurveFrom3Points(noCurve) : vowelNoPose ? buildTongueCenterline(vowelNoPose) : null;
    const vowelL1Path = l1Curve ? buildCurveFrom3Points(l1Curve) : vowelL1Pose ? buildTongueCenterline(vowelL1Pose) : null;

    const drawCurveHandles = (curve, color) => {
      if (!curve) return null;
      const s = pt(curve.start.x, curve.start.y);
      const e = pt(curve.end.x, curve.end.y);
      const r = large ? 9 : 7;
      return (
        <g opacity="0.9">
          <circle cx={s.x} cy={s.y} r={r} fill={color} opacity="0.85" />
          <circle cx={e.x} cy={e.y} r={r} fill={color} opacity="0.85" />
        </g>
      );
    };

    const VOWEL_LEGEND = hasVowelVis && (vowelNoPath || vowelL1Path) ? (
      <g opacity="0.95">
        <g transform={`translate(${pt(0.08, 0.84).x}, ${pt(0.08, 0.84).y})`}>
          <circle cx="0" cy="0" r={large ? 8 : 7} fill="#f97316" opacity="0.95" />
          <text
            x={large ? 16 : 14}
            y={large ? 6 : 5}
            fontSize={large ? 16 : 14}
            fontFamily="Inter, ui-sans-serif, system-ui, sans-serif"
            fontWeight="800"
            fill="#0f172a"
            opacity="0.75"
          >
            NO (Norwegian)
          </text>

          <circle cx="0" cy={large ? 26 : 22} r={large ? 8 : 7} fill="#2563eb" opacity="0.95" />
          <text
            x={large ? 16 : 14}
            y={(large ? 26 : 22) + (large ? 6 : 5)}
            fontSize={large ? 16 : 14}
            fontFamily="Inter, ui-sans-serif, system-ui, sans-serif"
            fontWeight="800"
            fill="#0f172a"
            opacity="0.75"
          >
            L1
          </text>

          <text
            x="0"
            y={large ? 48 : 42}
            fontSize={large ? 13 : 12}
            fontFamily="Inter, ui-sans-serif, system-ui, sans-serif"
            fontWeight="700"
            fill="#0f172a"
            opacity="0.45"
          >
            front/central/back + close/mid/open
          </text>
        </g>
      </g>
    ) : null;

    const COMPARE_LEGEND = hasCompareVis ? (
      <g opacity="0.95">
        <g transform={`translate(${pt(0.08, 0.76).x}, ${pt(0.08, 0.76).y})`}>
          {/* NO = orange */}
          <circle cx="0" cy="0" r={large ? 8 : 7} fill="#f97316" opacity="0.95" />
          <text
            x={large ? 16 : 14}
            y={large ? 6 : 5}
            fontSize={large ? 16 : 14}
            fontFamily="Inter, ui-sans-serif, system-ui, sans-serif"
            fontWeight="800"
            fill="#0f172a"
            opacity="0.75"
          >
            {visCompare?.noLabel || "NO"}
          </text>

          {/* L1 = blue */}
          <circle cx="0" cy={large ? 26 : 22} r={large ? 8 : 7} fill="#2563eb" opacity="0.95" />
          <text
            x={large ? 16 : 14}
            y={(large ? 26 : 22) + (large ? 6 : 5)}
            fontSize={large ? 16 : 14}
            fontFamily="Inter, ui-sans-serif, system-ui, sans-serif"
            fontWeight="800"
            fill="#0f172a"
            opacity="0.75"
          >
            {visCompare?.l1Label || "L1"}
          </text>

          {/* secondary articulation hint */}
          <circle cx="0" cy={large ? 52 : 44} r={large ? 8 : 7} fill="#8b5cf6" opacity="0.95" />
          <text
            x={large ? 16 : 14}
            y={(large ? 52 : 44) + (large ? 6 : 5)}
            fontSize={large ? 14 : 13}
            fontFamily="Inter, ui-sans-serif, system-ui, sans-serif"
            fontWeight="800"
            fill="#0f172a"
            opacity="0.55"
          >
            secondary (palatalized/velarized)
          </text>
        </g>
      </g>
    ) : null;

    return (
      <svg
        viewBox={`0 0 ${VB.w} ${VB.h}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Articulation diagram (side view)"
        style={{ width: "100%", height: "100%", display: "block" }}
      >
        <image href={baseSrc} x="0" y="0" width={VB.w} height={VB.h} preserveAspectRatio="xMidYMid meet" />

        {/* --- defs for breath ribbons + puff pulse --- */}
        <defs>
          <filter id="airBlur" x="-25%" y="-25%" width="150%" height="150%">
            <feGaussianBlur stdDeviation={large ? 2.2 : 1.6} />
          </filter>

          <linearGradient id="airGradBase" x1="1" y1="0" x2="0" y2="0">
            <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.78" />
            <stop offset="65%" stopColor="#38bdf8" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#38bdf8" stopOpacity="0" />
          </linearGradient>

          <linearGradient id="airGradPulseLoop" x1="1" y1="0" x2="0" y2="0">
            <stop offset="0%" stopColor="#38bdf8" stopOpacity="0" />
            <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.0">
              <animate attributeName="offset" values="0;1" dur="1.25s" repeatCount="indefinite" />
            </stop>
            <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.35">
              <animate attributeName="offset" values="0.06;1.06" dur="1.25s" repeatCount="indefinite" />
            </stop>
            <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.0">
              <animate attributeName="offset" values="0.14;1.14" dur="1.25s" repeatCount="indefinite" />
            </stop>
            <stop offset="100%" stopColor="#38bdf8" stopOpacity="0" />
          </linearGradient>

          <linearGradient id="airGradPulsePuff" x1="1" y1="0" x2="0" y2="0">
            <stop offset="0%" stopColor="#38bdf8" stopOpacity="0" />
            <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.0">
              <animate attributeName="offset" values="0;1" dur="0.9s" repeatCount="indefinite" />
            </stop>
            <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.45">
              <animate attributeName="offset" values="0.05;1.05" dur="0.9s" repeatCount="indefinite" />
            </stop>
            <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.0">
              <animate attributeName="offset" values="0.12;1.12" dur="0.9s" repeatCount="indefinite" />
            </stop>
            <stop offset="100%" stopColor="#38bdf8" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* landmarks */}
        {showLandmarks ? (
          <g opacity="0.85">
            {Object.entries(LANDMARKS).map(([k, p]) => (
              <g key={k}>
                <circle cx={p.x} cy={p.y} r={dotR} fill="#ffffff" opacity="0.92" />
                <circle cx={p.x} cy={p.y} r={dotR} fill="none" stroke="#0f172a" strokeWidth="2" opacity="0.35" />

                <text
                  x={p.x + 12}
                  y={p.y - 12}
                  fontSize={labelSize}
                  fontFamily="Inter, ui-sans-serif, system-ui, sans-serif"
                  fontWeight="700"
                  fill="none"
                  stroke="#0f172a"
                  strokeWidth={haloOuter}
                  opacity="0.28"
                  paintOrder="stroke"
                  letterSpacing="0.04em"
                >
                  {k.toUpperCase()}
                </text>

                <text
                  x={p.x + 12}
                  y={p.y - 12}
                  fontSize={labelSize}
                  fontFamily="Inter, ui-sans-serif, system-ui, sans-serif"
                  fontWeight="700"
                  fill="#0f172a"
                  stroke="#ffffff"
                  strokeWidth={haloInner}
                  opacity="0.98"
                  paintOrder="stroke"
                  letterSpacing="0.04em"
                >
                  {k.toUpperCase()}
                </text>
              </g>
            ))}
          </g>
        ) : null}

        {/* ------------------ vowel overlay curves (NO vs L1) ------------------ */}
        {hasVowelVis ? (
          <g>
            {vowelNoPath ? (
              <g opacity="0.98">
                <path
                  d={vowelNoPath}
                  fill="none"
                  stroke="#f97316"
                  strokeWidth={large ? 12 : 10}
                  strokeLinecap="round"
                  opacity="0.65"
                />
              </g>
            ) : null}

            {vowelL1Path ? (
              <g opacity="0.98">
                <path
                  d={vowelL1Path}
                  fill="none"
                  stroke="#2563eb"
                  strokeWidth={large ? 12 : 10}
                  strokeLinecap="round"
                  opacity="0.62"
                />
              </g>
            ) : null}

            {noCurve ? drawCurveHandles(noCurve, "#f97316") : null}
            {l1Curve ? drawCurveHandles(l1Curve, "#2563eb") : null}

            {VOWEL_LEGEND}
          </g>
        ) : null}

        {/* ------------------ consonant compare overlay (NO vs L1) -------------- */}
        {hasCompareVis ? (
          <g>
            {/* NO primary places (orange) */}
            {compareNoDots.map((p, i) => (
              <g key={`cmp-no-${i}`}>
                <circle cx={p.x} cy={p.y} r={large ? 54 : 42} fill="#fdba74" opacity="0.22" />
                <circle cx={p.x} cy={p.y} r={large ? 22 : 18} fill="#f97316" opacity="0.9" />
              </g>
            ))}

            {/* L1 primary places (blue) */}
            {compareL1Dots.map((p, i) => (
              <g key={`cmp-l1-${i}`}>
                <circle cx={p.x} cy={p.y} r={large ? 54 : 42} fill="#93c5fd" opacity="0.18" />
                <circle cx={p.x} cy={p.y} r={large ? 22 : 18} fill="#2563eb" opacity="0.9" />
              </g>
            ))}

            {/* Secondary targets: purple */}
            {compareNoSecDots.map((p, i) => (
              <g key={`cmp-no-sec-${i}`}>
                <circle cx={p.x} cy={p.y} r={large ? 38 : 30} fill="#c4b5fd" opacity="0.22" />
                <circle cx={p.x} cy={p.y} r={large ? 16 : 13} fill="#8b5cf6" opacity="0.85" />
              </g>
            ))}
            {compareL1SecDots.map((p, i) => (
              <g key={`cmp-l1-sec-${i}`}>
                <circle cx={p.x} cy={p.y} r={large ? 38 : 30} fill="#c4b5fd" opacity="0.22" />
                <circle cx={p.x} cy={p.y} r={large ? 16 : 13} fill="#8b5cf6" opacity="0.85" />
              </g>
            ))}

            {COMPARE_LEGEND}
          </g>
        ) : null}

        {/* ------------------ single consonant vis (RU-style) ------------------- */}
        {!hasCompareVis ? (
          <>
            {/* primary (blue) */}
            {primaryDots.length ? (
              <g>
                {primaryDots.map((p, i) => (
                  <g key={`pri-${i}`}>
                    <circle cx={p.x} cy={p.y} r={large ? 54 : 42} fill="#93c5fd" opacity="0.22" />
                    <circle cx={p.x} cy={p.y} r={large ? 22 : 18} fill="#60a5fa" opacity="0.85" />
                  </g>
                ))}
              </g>
            ) : null}

            {/* secondary (purple) */}
            {secondaryDots.length ? (
              <g>
                {secondaryDots.map((p, i) => (
                  <g key={`sec-${i}`}>
                    <circle cx={p.x} cy={p.y} r={large ? 38 : 30} fill="#c4b5fd" opacity="0.22" />
                    <circle cx={p.x} cy={p.y} r={large ? 16 : 13} fill="#8b5cf6" opacity="0.85" />
                  </g>
                ))}
              </g>
            ) : null}

            {/* tongue overlay (optional) */}
            {showTongue && (secondaryDots.length > 0 || primaryPlaces.length > 0 || pose.contact !== "none") ? (
              <path d={tonguePath} fill="#fde68a" stroke="#f59e0b" strokeWidth={large ? 8 : 6} opacity="0.92" />
            ) : null}

            {/* airflow */}
            {air ? (
              <g opacity={air.opacity}>
                {(() => {
                  const lips = LANDMARKS.lips || pt(0.2, 0.51);
                  const x0 = lips.x - (large ? 4 : 3);
                  const y0 = lips.y + (large ? -30 : -30);

                  const length =
                    air.kind === "burst" ? (large ? 175 : 145) : air.kind === "noisy" ? (large ? 235 : 200) : (large ? 220 : 185);

                  const x1 = x0 - length;
                  const y1 = y0;

                  const w0 =
                    air.kind === "burst" ? (large ? 40 : 32) : air.kind === "noisy" ? (large ? 30 : 24) : (large ? 26 : 21);

                  const w1 = Math.max(9, w0 * (air.kind === "burst" ? 0.55 : 0.35));

                  const c1x = x0 - length * 0.28;
                  const c2x = x0 - length * 0.62;

                  const wobble = air.kind === "noisy" ? (large ? 14 : 10) : (large ? 6 : 4);

                  const top = `
                    M ${x0} ${y0 - w0 / 2}
                    C ${c1x} ${y0 - w0 / 2 - wobble},
                      ${c2x} ${y0 - w1 / 2 + wobble},
                      ${x1} ${y1 - w1 / 2}
                  `;

                  const bottom = `
                    C ${c2x} ${y0 + w1 / 2 + wobble},
                      ${c1x} ${y0 + w0 / 2 - wobble},
                      ${x0} ${y0 + w0 / 2}
                    Z
                  `;

                  const d = `${top} ${bottom}`;

                  const noisyAnim =
                    air.kind === "noisy" ? (
                      <animateTransform
                        attributeName="transform"
                        type="translate"
                        values={`0 0; -2 1; 1 -1; -1 0; 0 0`}
                        dur={large ? "0.35s" : "0.45s"}
                        repeatCount="indefinite"
                      />
                    ) : null;

                  const puffAnim =
                    air.kind === "burst" ? (
                      <>
                        <animateTransform
                          attributeName="transform"
                          type="translate"
                          additive="sum"
                          values={`0 0; -6 0; 0 0`}
                          dur={large ? "0.95s" : "1.05s"}
                          repeatCount="indefinite"
                        />
                        <animateTransform
                          attributeName="transform"
                          type="scale"
                          additive="sum"
                          values={`1; 1.08; 1`}
                          dur={large ? "0.95s" : "1.05s"}
                          repeatCount="indefinite"
                        />
                        <animate attributeName="opacity" values="1;0.15;1" dur={large ? "0.95s" : "1.05s"} repeatCount="indefinite" />
                      </>
                    ) : null;

                  const pulseId = air.kind === "burst" ? "airGradPulsePuff" : "airGradPulseLoop";

                  return (
                    <g filter="url(#airBlur)">
                      <path d={d} fill="url(#airGradBase)" />
                      <path d={d} fill={`url(#${pulseId})`} opacity="0.9" />
                      {noisyAnim}
                      {puffAnim}
                    </g>
                  );
                })()}
              </g>
            ) : null}

            {/* voicing */}
            {pose.voicing ? (
              <g opacity="0.95">
                {(() => {
                  const cxp = pt(0.72, 0.8).x;
                  const cyp = pt(0.72, 0.8).y;

                  return (
                    <g>
                      <circle cx={cxp} cy={cyp} r={large ? 30 : 26} fill="#ffe4e6" stroke="#fb7185" strokeWidth={large ? 6 : 5} />
                      <path
                        d={`M ${cxp - 16} ${cyp} c 8 -14 16 14 24 0 c 8 -14 16 14 24 0`}
                        fill="none"
                        stroke="#e11d48"
                        strokeWidth={large ? 6 : 5}
                        strokeLinecap="round"
                      />
                      <text
                        x={cxp + (large ? 40 : 34)}
                        y={cyp + (large ? 8 : 6)}
                        fontSize={large ? 18 : 16}
                        fontFamily="Inter, ui-sans-serif, system-ui, sans-serif"
                        fontWeight="800"
                        fill="#e11d48"
                        opacity="0.95"
                        letterSpacing="0.04em"
                      >
                        VOICED
                      </text>
                    </g>
                  );
                })()}
              </g>
            ) : null}

            {/* devoicing indicator */}
            {meta?.devoiceFinal ? (
              <g opacity="0.7">
                <circle
                  cx={pt(0.92, 0.12).x}
                  cy={pt(0.92, 0.12).y}
                  r={large ? 22 : 18}
                  fill="#f1f5f9"
                  stroke="#94a3b8"
                  strokeWidth={large ? 5 : 4}
                />
                <path
                  d={`M ${pt(0.90, 0.12).x} ${pt(0.12, 0.12).y} l 28 0`}
                  fill="none"
                  stroke="#94a3b8"
                  strokeWidth={large ? 6 : 5}
                  strokeLinecap="round"
                />
              </g>
            ) : null}
          </>
        ) : null}
      </svg>
    );
  };

  return (
    <div className={className}>
      {label ? <div className="mb-2 text-sm font-semibold text-slate-900">{label}</div> : null}

      <div className="rounded-2xl border bg-white p-4 shadow-sm">
        <div
          role={enableZoom ? "button" : undefined}
          tabIndex={enableZoom ? 0 : undefined}
          onClick={openZoom}
          onKeyDown={(e) => {
            if (!enableZoom) return;
            if (e.key === "Enter" || e.key === " ") openZoom();
          }}
          className={
            enableZoom
              ? "group w-full cursor-zoom-in rounded-xl outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2"
              : "w-full"
          }
          title={enableZoom ? "Click to enlarge" : undefined}
        >
          <div className="h-[280px] w-full sm:h-[320px]">
            <DiagramSvg large={false} />
          </div>

          {enableZoom ? (
            <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
              <span>{captionText}</span>
              <span className="rounded-full border bg-white px-2 py-0.5 text-[11px] text-slate-600 opacity-80 group-hover:opacity-100">
                Click to enlarge
              </span>
            </div>
          ) : (
            <div className="mt-2 text-xs text-slate-700">{captionText}</div>
          )}
        </div>
      </div>

      <ZoomModal open={zoomOpen} title="Articulation (zoom)" onClose={closeZoom}>
        <div className="rounded-2xl border bg-white p-3 shadow-sm">
          <div className="aspect-square max-h-[75vh] w-full">
            <DiagramSvg large />
          </div>
          <div className="mt-2 text-xs text-slate-700">{captionText}</div>
        </div>
      </ZoomModal>
    </div>
  );
}
